import { useCallback, useEffect, useRef } from "react";

const ROW_SELECTOR = "[data-dl-row-index]";
const ALIGN_OFFSET_PX = 8;
const RESIZE_DEBOUNCE_MS = 50;

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function getOffsetTopWithin(el, scrollContainer) {
	const containerRect = scrollContainer.getBoundingClientRect();
	const elRect = el.getBoundingClientRect();
	return elRect.top - containerRect.top + scrollContainer.scrollTop;
}

function getTopRowIndex(scrollEl) {
	const rows = scrollEl.querySelectorAll(ROW_SELECTOR);
	if (rows.length === 0) return null;

	const scrollRect = scrollEl.getBoundingClientRect();
	let index = Number(rows[0].dataset.dlRowIndex ?? 0);

	for (const row of rows) {
		const top = row.getBoundingClientRect().top - scrollRect.top;
		if (top <= ALIGN_OFFSET_PX + 2) {
			index = Number(row.dataset.dlRowIndex ?? index);
		} else {
			break;
		}
	}

	return index;
}

function getRowByIndex(scrollEl, rowIndex) {
	return scrollEl.querySelector(`[data-dl-row-index="${rowIndex}"]`);
}

export function getScrollAnchor(scrollEl) {
	const rowIndex = getTopRowIndex(scrollEl);
	if (rowIndex === null) return null;

	const row = getRowByIndex(scrollEl, rowIndex);
	if (!row) return { rowIndex, ratio: 0 };

	const rowTop = getOffsetTopWithin(row, scrollEl);
	const rowHeight = row.offsetHeight;
	if (rowHeight <= 0) {
		return { rowIndex, ratio: 0 };
	}

	const ratio = clamp(
		(scrollEl.scrollTop - rowTop + ALIGN_OFFSET_PX) / rowHeight,
		0,
		1
	);

	return { rowIndex, ratio };
}

export function applyScrollAnchor(scrollEl, rowIndex, ratio) {
	let row = getRowByIndex(scrollEl, rowIndex);

	if (!row) {
		const rows = scrollEl.querySelectorAll(ROW_SELECTOR);
		row = rows.length > 0 ? rows[rows.length - 1] : null;
		if (!row) return;
		rowIndex = Number(row.dataset.dlRowIndex ?? rowIndex);
	}

	const rowTop = getOffsetTopWithin(row, scrollEl);
	const rowHeight = row.offsetHeight;
	const clampedRatio = clamp(ratio, 0, 1);
	scrollEl.scrollTop = rowTop + clampedRatio * rowHeight - ALIGN_OFFSET_PX;
}

export function useSyncedTripleBlockScroll(deps = []) {
	const aiRef = useRef(null);
	const humanRef = useRef(null);
	const advancedRef = useRef(null);
	const syncingRef = useRef(null);
	const lastAnchorRef = useRef(null);

	const getRefForSide = useCallback((side) => {
		if (side === "ai") return aiRef.current;
		if (side === "human") return humanRef.current;
		return advancedRef.current;
	}, []);

	const applyAnchorToOthers = useCallback((sourceSide, anchor) => {
		const refs = [
			{ side: "ai", el: aiRef.current },
			{ side: "human", el: humanRef.current },
			{ side: "advanced", el: advancedRef.current },
		];

		syncingRef.current = sourceSide;
		for (const { side, el } of refs) {
			if (side === sourceSide || !el) continue;
			applyScrollAnchor(el, anchor.rowIndex, anchor.ratio);
		}
		requestAnimationFrame(() => {
			syncingRef.current = null;
		});
	}, []);

	const syncFrom = useCallback(
		(source, sourceSide) => {
			if (syncingRef.current && syncingRef.current !== sourceSide) return;

			const anchor = getScrollAnchor(source);
			if (!anchor) return;

			lastAnchorRef.current = { side: sourceSide, anchor };
			applyAnchorToOthers(sourceSide, anchor);
		},
		[applyAnchorToOthers]
	);

	const syncAllFromSide = useCallback(
		(sourceSide) => {
			const source = getRefForSide(sourceSide);
			if (!source) return;

			const anchor = getScrollAnchor(source);
			if (!anchor) return;

			lastAnchorRef.current = { side: sourceSide, anchor };
			applyAnchorToOthers(sourceSide, anchor);
		},
		[applyAnchorToOthers, getRefForSide]
	);

	const resetScroll = useCallback(() => {
		lastAnchorRef.current = null;
		for (const ref of [aiRef, humanRef, advancedRef]) {
			ref.current?.scrollTo(0, 0);
		}
	}, []);

	useEffect(() => {
		const ai = aiRef.current;
		const human = humanRef.current;
		const advanced = advancedRef.current;
		if (!ai || !human || !advanced) return undefined;

		const onAiScroll = () => syncFrom(ai, "ai");
		const onHumanScroll = () => syncFrom(human, "human");
		const onAdvancedScroll = () => syncFrom(advanced, "advanced");

		ai.addEventListener("scroll", onAiScroll, { passive: true });
		human.addEventListener("scroll", onHumanScroll, { passive: true });
		advanced.addEventListener("scroll", onAdvancedScroll, { passive: true });

		return () => {
			ai.removeEventListener("scroll", onAiScroll);
			human.removeEventListener("scroll", onHumanScroll);
			advanced.removeEventListener("scroll", onAdvancedScroll);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [syncFrom, ...deps]);

	useEffect(() => {
		const containers = [aiRef.current, humanRef.current, advancedRef.current].filter(Boolean);
		if (containers.length === 0) return undefined;

		let debounceTimer = null;

		const resyncFromLastAnchor = () => {
			const last = lastAnchorRef.current;
			if (!last) return;
			applyAnchorToOthers(last.side, last.anchor);
		};

		const observer = new ResizeObserver(() => {
			if (debounceTimer) clearTimeout(debounceTimer);
			debounceTimer = setTimeout(resyncFromLastAnchor, RESIZE_DEBOUNCE_MS);
		});

		for (const container of containers) {
			observer.observe(container);
		}

		return () => {
			if (debounceTimer) clearTimeout(debounceTimer);
			observer.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applyAnchorToOthers, ...deps]);

	useEffect(() => {
		resetScroll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetScroll, ...deps]);

	return { aiRef, humanRef, advancedRef, resetScroll, syncAllFromSide };
}
